import { uniqBy } from 'lodash'
import { createSelector } from 'reselect'

import API from '../../api/api'

// Action Types
const GET_BOOK_TYPES = 'GET_BOOK_TYPES'
const GET_BOOK_TYPE = 'GET_BOOK_TYPE'
const GET_BOOK_TYPES_FAIL = 'GET_BOOK_TYPES_FAIL'
const GET_BOOK_TYPES_SUCCESS = 'GET_BOOK_TYPES_SUCCESS'
const SET_ALL_BOOKING_TYPES = 'SET_ALL_BOOKING_TYPES'
const ADD_BOOK_TYPE = 'ADD_BOOK_TYPE'
const REMOVE_BOOK_TYPE = 'REMOVE_BOOK_TYPE'
const UPDATE_BOOK_TYPE = 'UPDATE_BOOK_TYPE'
const REORDER_BOOK_TYPES = 'REORDER_BOOK_TYPES'

// Initial state
const initialState = {
  loading: false,
  loadedAll: false,
  error: '',
  page: 0,
  data: [],
}

// Reducer
const bookTypesReducer = (state = initialState, action) => {
  switch (action.type) {
    case GET_BOOK_TYPES:
    case GET_BOOK_TYPES_FAIL:
    case GET_BOOK_TYPES_SUCCESS: {
      return {
        ...state,
        ...action.payload,
      }
    }
    case ADD_BOOK_TYPE: {
      return {
        ...state,
        data: [action.payload.data, ...state.data],
      }
    }
    case SET_ALL_BOOKING_TYPES: {
      const currentBookingTypes = state.data
      const newBookingTypes = action.payload

      const mergedBookingTypes = newBookingTypes.map((newBookingType) => {
        const currentBookingType = currentBookingTypes.find(
          (bookingType) => bookingType.id === newBookingType.id,
        )

        if (currentBookingType) {
          return {
            ...currentBookingType,
            ...newBookingType,
          }
        } else {
          return newBookingType
        }
      })
      return {
        ...state,
        data: mergedBookingTypes,
      }
    }
    case REMOVE_BOOK_TYPE: {
      let typesArray = [...state.data]
      typesArray = typesArray.filter((el) => el.id !== action.payload.id)
      return {
        ...state,
        data: typesArray,
      }
    }
    case UPDATE_BOOK_TYPE:
    case GET_BOOK_TYPE: {
      const updatedBookingTypes = [...state.data].map((el) => {
        if (el.id === action.payload.data.id) {
          return { ...el, ...action.payload.data }
        }
        return el
      })
      return {
        ...state,
        data: updatedBookingTypes,
      }
    }
    case REORDER_BOOK_TYPES: {
      const ordersById = new Map(action.payload.map(({ id, order }) => [id, order]))
      return {
        ...state,
        data: state.data.map((el) =>
          ordersById.has(el.id) ? { ...el, order: ordersById.get(el.id) } : el,
        ),
      }
    }
  }
  return state
}

// Actions
export const fetchBookTypes = () => {
  return (dispatch, getState) => {
    dispatch({
      type: GET_BOOK_TYPES,
      payload: {
        loading: true,
      },
    })
    const PAGE_SIZE = 10
    const page = Math.floor(selectBookTypes(getState()).length / PAGE_SIZE)
    return API.getBookingTypes(page)
      .then((res) => {
        dispatch({
          type: GET_BOOK_TYPES_SUCCESS,
          payload: {
            loading: false,
            data: uniqBy([...selectBookTypes(getState()), ...res], 'id'),
            page: page,
            loadedAll: res.length < 10,
            error: '',
          },
        })
        return Promise.resolve(res)
      })
      .catch((err) => {
        dispatch({
          type: GET_BOOK_TYPES_FAIL,
          payload: {
            loading: false,
            error: err,
          },
        })
        return Promise.reject(err)
      })
  }
}

export const fetchAllBookingTypes = () => {
  return (dispatch) => {
    dispatch({
      type: GET_BOOK_TYPES,
      payload: {
        loading: true,
      },
    })

    return API.getBookingClient()
      .then((res) => {
        dispatch({
          type: SET_ALL_BOOKING_TYPES,
          payload: res,
        })
        return Promise.resolve(res)
      })
      .catch((err) => {
        return Promise.reject(err)
      })
      .finally(() =>
        dispatch({
          type: GET_BOOK_TYPES,
          payload: {
            loading: false,
          },
        }),
      )
  }
}

export const fetchAllBookingTypesOnly = () => {
  return (dispatch) => {
    dispatch({
      type: GET_BOOK_TYPES,
      payload: {
        loading: true,
      },
    })

    API.getBookingTypes('all')
      .then((res) => {
        dispatch({
          type: SET_ALL_BOOKING_TYPES,
          payload: res,
        })
        return Promise.resolve(res)
      })
      .catch((err) => {
        return Promise.reject(err)
      })
      .finally(() =>
        dispatch({
          type: GET_BOOK_TYPES,
          payload: {
            loading: false,
          },
        }),
      )
  }
}

export const fetchOneBookingTypeOnly = (bookingId) => {
  return (dispatch) => {
    API.getBookingType(bookingId)
      .then((res) => {
        dispatch({
          type: GET_BOOK_TYPE,
          payload: {
            data: { ...res, category: res.category.id },
          },
        })
        return Promise.resolve(res)
      })
      .catch((err) => {
        return Promise.reject(err)
      })
  }
}

export const addBookType = (data) => {
  return (dispatch) => {
    return API.createBookingType(data)
      .then((res) => {
        dispatch({
          type: ADD_BOOK_TYPE,
          payload: {
            data: { ...res, category: res.category.id },
          },
        })
        return Promise.resolve()
      })
      .catch((err) => {
        return Promise.reject(err)
      })
  }
}

export const removeBookType = (id) => {
  return (dispatch) => {
    return API.deleteBookingType(id)
      .then(() => {
        dispatch({
          type: REMOVE_BOOK_TYPE,
          payload: {
            id: id,
          },
        })
        return Promise.resolve()
      })
      .catch((err) => {
        return Promise.reject(err)
      })
  }
}

export const updateBookType = (id, updateData) => {
  return (dispatch) => {
    const data = { ...updateData }

    if (updateData.buildings) {
      data.buildings = JSON.stringify(updateData.buildings.map((building) => building.id))
    }

    if (updateData.image) {
      data.image = updateData.image.file
    }

    return API.updateBookingType(id, data)
      .then((res) => {
        dispatch({
          type: UPDATE_BOOK_TYPE,
          payload: {
            data: { ...res, id: parseInt(res.id) },
          },
        })
        return Promise.resolve(res)
      })
      .catch((err) => {
        return Promise.reject(err)
      })
  }
}

// Lightweight partial update used by Up/Down reordering — mirrors editBookingCategory:
// sends only the order field so it can't collide with updateBookType's image/buildings
// shape-specific handling, which expects the full edit-form payload. Deliberately does NOT
// re-dispatch the server's response on success: reorderBookTypesLocally already applied the
// correct order optimistically, and re-syncing here would let a slow response from an earlier
// drag stomp a newer one that already changed this item's order client-side (two quick drags
// racing) — this PUT exists purely to persist, not to drive local state.
export const updateBookTypeOrder = (id, order) => {
  return () => {
    return API.updateBookingType(id, { order }).catch((err) => {
      return Promise.reject(err)
    })
  }
}

// Applies new `order` values to Redux state immediately, so Up/Down clicks and drag-and-drop
// reordering reflect on screen without waiting for updateBookTypeOrder's round trip.
export const reorderBookTypesLocally = (updates) => ({
  type: REORDER_BOOK_TYPES,
  payload: updates,
})

export const getBookTypesByBuildingId = (buildingId) => (dispatch) => {
  dispatch({
    type: GET_BOOK_TYPES,
    payload: {
      loading: true,
    },
  })

  return API.getBookingTypesByBuildingId(buildingId)
    .then((res) => {
      const { data } = res
      dispatch({
        type: SET_ALL_BOOKING_TYPES,
        payload: data,
      })
      return Promise.resolve(data)
    })
    .catch((err) => {
      return Promise.reject(err)
    })
    .finally(() =>
      dispatch({
        type: GET_BOOK_TYPES,
        payload: {
          loading: false,
        },
      }),
    )
}

export const sortBookTypesByCategory = (bookingTypes) => {
  const categories = uniqBy(bookingTypes, 'category').map((type) => [type.category, []])
  const sortedObject = Object.fromEntries(categories)

  bookingTypes.filter((item) => {
    sortedObject[item.category].push(item)
  })

  return sortedObject
}

// Tie-broken by `id` so the visual order stays deterministic even when two items share the
// same `order` value (e.g. a gap-ordering midpoint that happens to collide with a pre-existing
// dense integer order) — MySQL's `ORDER BY order ASC` has no defined tie-break, so without this
// a collision can make the fetched row order (and therefore what's rendered) differ between
// requests even though nothing changed.
export const sortByOrder = (items) => {
  return [...items].sort((a, b) => a.order - b.order || a.id - b.id)
}

export default bookTypesReducer

export const selectBookTypes = (state) => state.bookTypes.data
export const selectBookTypesLoading = (state) => state.bookTypes.loading
export const selectBookTypesLoadedAll = (state) => state.bookTypes.loadedAll

export const selectActiveBookTypes = createSelector(selectBookTypes, (bookingTypes) =>
  bookingTypes.filter((bookingType) => bookingType.active === true),
)

export const selectBookingTypesForDropdown = createSelector(selectBookTypes, (bookingTypes) =>
  bookingTypes.map(({ id, name }) => ({ id, text: name })),
)

export const selectBookTypeById = createSelector(selectBookTypes, (bookingTypes) => (bookingId) => {
  if (!bookingId || bookingId === 'new') {
    return {}
  }
  return bookingTypes.find(({ id }) => id === parseInt(bookingId)) || {}
})

export const selectBookTypesByCategory = createSelector(selectActiveBookTypes, (bookingTypes) =>
  sortBookTypesByCategory(bookingTypes),
)
